/**
 * socialExport — render share-ready PNGs for social media from artworks and the
 * prompt-theme stats, using the site's own type (Delcan Mono / FK Grotesk Mono /
 * Martina Plantijn) and the B&W halftone chart language.
 *
 * Formats: story 1080×1920 · post 1080×1350 · landscape 1920×1080. Styles:
 * light · dark · black · poster (poster = drawing edge-to-edge at top, story
 * format only). renderArtworkCanvas gives the canvas back without downloading,
 * so the prompts video can draw the same frames.
 *
 * Everything renders on an offscreen <canvas> and downloads as PNG. R2 serves
 * images with Access-Control-Allow-Origin:* so drawing them does not taint the
 * canvas; base64 `imageData` artworks need no CORS at all.
 */

import type { PromptTheme } from '@/utils/promptThemes';

export type SocialFormat = 'story' | 'post' | 'landscape';
export type SocialStyle = 'light' | 'dark' | 'black' | 'poster';

export const SIZES: Record<SocialFormat, [number, number]> = {
  story: [1080, 1920],
  post: [1080, 1350],
  landscape: [1920, 1080],
};

const SITE_BG = '#f5f5f5';
const CANVAS_BG = '#f4efe9';
const INK = '#1a1a1a';
const MUTED = '#777777';
const BORDER = '#e5e5e5';
const DARK_BG = '#1a1a1a';
const DARK_INK = '#f2f0ed';
const DARK_MUTED = '#999999';
const DARK_BORDER = '#3a3a3a';
const BLACK_BG = '#000000';      // for a screen: the drawing's cream card on true black
const BLACK_BORDER = '#262626';
const CHART_OUTLINE = '#555555'; // dark-gray outline for halftone chart shapes (both styles)

const DELCAN = "'Delcan Mono', monospace";
const FKMONO = "'FK Grotesk Mono', monospace";

/** Make sure the brand fonts are in the document cache before drawing to canvas. */
async function ensureFonts(): Promise<void> {
  await Promise.all([
    document.fonts.load(`54px ${DELCAN}`),
    document.fonts.load(`34px ${FKMONO}`),
  ]);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the drawing image'));
    img.src = src;
  });
}

/** Draw text with letter-spacing; returns total width. centerX centers on that x. */
function tracked(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  tracking: number,
  centerX?: number
): number {
  const widths = [...text].map((ch) => ctx.measureText(ch).width);
  const total = widths.reduce((a, b) => a + b, 0) + tracking * (text.length - 1);
  let cx = centerX !== undefined ? centerX - total / 2 : x;
  [...text].forEach((ch, i) => {
    ctx.fillText(ch, cx, y);
    cx += widths[i] + tracking;
  });
  return total;
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxw: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const t = (cur + ' ' + w).trim();
    if (ctx.measureText(t).width <= maxw) cur = t;
    else {
      if (cur) lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

/** A repeating halftone dot tile (tile px square, dot radius r) in the given ink. */
function dotPattern(
  ctx: CanvasRenderingContext2D,
  tile: number,
  r: number,
  ink: string
): CanvasPattern {
  const t = document.createElement('canvas');
  t.width = t.height = tile;
  const tc = t.getContext('2d')!;
  tc.fillStyle = ink;
  tc.beginPath();
  tc.arc(tile / 2, tile / 2, r, 0, Math.PI * 2);
  tc.fill();
  return ctx.createPattern(t, 'repeat')!;
}

/** A halftone-filled rectangle with a crisp ink outline. */
function halftoneRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  tile: number,
  r: number,
  ink: string
) {
  ctx.fillStyle = dotPattern(ctx, tile, r, ink);
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = CHART_OUTLINE;
  ctx.lineWidth = 2.5;
  ctx.strokeRect(x, y, w, h);
}

// Halftone tones, dense -> sparse, assigned by rank.
const TONES: [number, number][] = [
  [9, 4.4],
  [9, 3.5],
  [10, 3.0],
  [10, 2.4],
  [11, 2.0],
  [11, 1.6],
];

function download(canvas: HTMLCanvasElement, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('Export failed (canvas could not encode)'));
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      resolve();
    }, 'image/png');
  });
}

export interface ShareArtwork {
  imageUrl?: string | null;
  imageData?: string;
  promptText?: string;
  promptNumber?: number | null;
  artistName?: string | null;
}

/** A small lucide-style "person" glyph (outline) in an s×s box at (x, y). */
function drawPersonIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  s: number,
  color: string
) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, s * 0.085);
  ctx.lineCap = 'round';
  const cx = x + s / 2;
  ctx.beginPath();
  ctx.arc(cx, y + s * 0.3, s * 0.19, 0, Math.PI * 2); // head
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, y + s * 1.02, s * 0.4, Math.PI * 1.2, Math.PI * 1.8); // shoulders
  ctx.stroke();
  ctx.restore();
}

/** Options shared by the export functions. */
export interface ExportOpts {
  /** Show the "Prompt-Brush 2.0 / Prompt-Brush.com" branding text. Default: false. */
  header?: boolean;
}

/** Render one artwork as a social image and hand back the canvas. */
export async function renderArtworkCanvas(
  art: ShareArtwork,
  format: SocialFormat,
  style: SocialStyle,
  opts: ExportOpts = {}
): Promise<HTMLCanvasElement> {
  const { header = false } = opts;
  await ensureFonts();
  const [W, H] = SIZES[format];
  const black = style === 'black';
  const dark = style === 'dark' || black;
  const poster = style === 'poster' && format === 'story';
  const bg = black ? BLACK_BG : dark ? DARK_BG : SITE_BG;
  const ink = dark ? DARK_INK : INK;
  const muted = dark ? DARK_MUTED : MUTED;
  const border = black ? BLACK_BORDER : dark ? DARK_BORDER : BORDER;
  const M = 72;

  const src = art.imageUrl || art.imageData || '';
  if (!src) throw new Error('This artwork has no image to export');
  const img = await loadImage(src);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = 'top';

  // Story carries the full header (wordmark left + URL right) and respects Instagram's
  // UI safe zones (~250 top / ~310 bottom). The post format is drawing-first — no
  // header or URL — so the artwork runs much larger.
  const story = format === 'story';
  const safeTop = story ? 250 : M;
  const safeBottom = story ? 310 : M;
  const iconS = 30;

  const drawCredit = (cx: number, cy: number) => {
    if (!art.artistName) return;
    drawPersonIcon(ctx, cx, cy - 2, iconS, muted);
    ctx.fillStyle = muted;
    ctx.font = `26px ${FKMONO}`;
    tracked(ctx, art.artistName.toUpperCase(), cx + iconS + 14, cy, 4);
  };

  const pSize = poster ? 56 : 64;
  const lineH = poster ? 70 : 80;

  if (format === 'landscape') {
    // Landscape — a frame for a screen, after the 1.0 films: the card centred on
    // the ground, the prompt typed once above it ("prompt:" dimmed, the words
    // bright), the artist's name in the bottom-left corner. The card is sized
    // from what the prompt leaves it, so a long prompt wraps and the card
    // gives way rather than the type shrinking.
    const ML = 64;
    const tSize = 34;
    const tLineH = 44;
    const top = 50;
    ctx.font = `${tSize}px ${DELCAN}`;
    const label = 'prompt: ';
    const labelW = ctx.measureText(label).width;
    const lines = wrap(ctx, art.promptText || 'Untitled', 1400 - labelW);
    let y = top;
    lines.forEach((ln, i) => {
      const w = ctx.measureText(ln).width + (i === 0 ? labelW : 0);
      let x = Math.round((W - w) / 2);
      if (i === 0) {
        ctx.fillStyle = muted;
        ctx.fillText(label, x, y);
        x += labelW;
      }
      ctx.fillStyle = ink;
      ctx.fillText(ln, x, y);
      y += tLineH;
    });

    const cardY = y + 6;
    const cardH = H - 86 - cardY;
    const cardW = Math.round((cardH * 5) / 7);
    const cardX = Math.round((W - cardW) / 2);
    if (!black) {
      ctx.strokeStyle = border;
      ctx.lineWidth = 2;
      ctx.strokeRect(cardX - 2, cardY - 2, cardW + 4, cardH + 4);
    }
    ctx.fillStyle = CANVAS_BG;
    ctx.fillRect(cardX, cardY, cardW, cardH);
    const s = Math.min(cardW / img.width, cardH / img.height);
    const aw = img.width * s;
    const ah = img.height * s;
    ctx.drawImage(img, cardX + (cardW - aw) / 2, cardY + (cardH - ah) / 2, aw, ah);

    const cy = H - 40 - iconS;
    drawCredit(ML, cy);
    if (header) {
      ctx.fillStyle = muted;
      ctx.font = `28px ${DELCAN}`;
      const url = 'Prompt-Brush.com';
      ctx.fillText(url, W - ML - ctx.measureText(url).width, cy + 2);
    }
  } else if (poster) {
    // Drawing edge-to-edge at the top, type band below (flush left at the margin).
    const ah = Math.round((img.height / img.width) * W);
    ctx.drawImage(img, 0, 0, W, ah);
    ctx.font = `${pSize}px ${DELCAN}`;
    const lines = wrap(ctx, art.promptText || 'Untitled', W - 2 * M);
    let y = ah + 70;
    ctx.fillStyle = ink;
    for (const ln of lines) {
      ctx.fillText(ln, M, y);
      y += lineH;
    }
    drawCredit(M, y + 16);
    if (header) {
      ctx.fillStyle = ink;
      ctx.font = `40px ${DELCAN}`;
      ctx.fillText('Prompt-Brush 2.0', M, H - safeBottom - 40);
    }
  } else {
    // Header — Delcan Mono wordmark left, URL right (story only, when enabled).
    let top = story ? safeTop : M;
    if (story && header) {
      ctx.fillStyle = ink;
      ctx.font = `40px ${DELCAN}`;
      ctx.fillText('Prompt-Brush 2.0', M, safeTop);
      const url = 'Prompt-Brush.com';
      ctx.fillText(url, W - M - ctx.measureText(url).width, safeTop);
      top = safeTop + 96;
    }
    // Fixed-size, fixed-position card so EVERY export places the drawing identically,
    // no matter how many lines the prompt wraps to. We reserve room for a 2-line
    // prompt; the prompt then wraps to the card's width and grows downward from there.
    const RESERVED_LINES = 2;
    const reserve = 60 + RESERVED_LINES * lineH + 18 + 40;
    let cardH = Math.max(400, H - safeBottom - top - reserve);
    const cardW = Math.min(W - 2 * M, Math.round((cardH * 5) / 7));
    cardH = Math.round((cardW * 7) / 5);
    const cardX = Math.round((W - cardW) / 2); // centered, constant for all exports
    ctx.font = `${pSize}px ${DELCAN}`;
    const lines = wrap(ctx, art.promptText || 'Untitled', cardW);

    ctx.strokeStyle = border;
    ctx.lineWidth = 2;
    ctx.strokeRect(cardX - 2, top - 2, cardW + 4, cardH + 4);
    ctx.fillStyle = CANVAS_BG;
    ctx.fillRect(cardX, top, cardW, cardH);
    const s = Math.min(cardW / img.width, cardH / img.height);
    const aw = img.width * s;
    const ah = img.height * s;
    ctx.drawImage(img, cardX + (cardW - aw) / 2, top + (cardH - ah) / 2, aw, ah);

    // Prompt + credit, aligned to the card's left edge.
    let y = top + cardH + 60;
    ctx.fillStyle = ink;
    ctx.font = `${pSize}px ${DELCAN}`;
    for (const ln of lines) {
      ctx.fillText(ln, cardX, y);
      y += lineH;
    }
    drawCredit(cardX, y + 18);
  }

  return canvas;
}

/** Export one artwork as a social PNG and trigger the download. */
export async function exportArtworkImage(
  art: ShareArtwork,
  format: SocialFormat,
  style: SocialStyle,
  opts: ExportOpts = {}
): Promise<void> {
  const canvas = await renderArtworkCanvas(art, format, style, opts);
  const numPart = art.promptNumber ? `-${String(art.promptNumber).padStart(4, '0')}` : '';
  await download(canvas, `prompt-brush${numPart}-${format}-${style}.png`);
}

export type ChartKind = 'donut' | 'columns' | 'bars';
export interface ChartSpec {
  kind: ChartKind;
  title: string;
  items: PromptTheme[];
  footnote: string;
  slug: string;
}

export interface ChartDrawOpts extends ExportOpts {
  /** Build progress 0→1 for the animated video; 1 (the default) is the finished chart. */
  progress?: number;
}

/**
 * Draw one halftone stats chart (donut / column histogram / ranked bars) onto a
 * context the size of SIZES[format]. Story respects Instagram's safe zones; post
 * is the plain vertical; landscape sets the donut beside its legend and fits the
 * ranked bars to the frame's height. `progress` sweeps the donut, grows the bars
 * and counts the numbers up, so the stats video can draw the same chart frame by frame.
 */
export function drawChart(
  ctx: CanvasRenderingContext2D,
  spec: ChartSpec,
  format: SocialFormat,
  style: Exclude<SocialStyle, 'poster'>,
  opts: ChartDrawOpts = {}
): void {
  const { header = false, progress = 1 } = opts;
  const { kind, title, items, footnote } = spec;
  const [W, H] = SIZES[format];
  const dark = style === 'dark' || style === 'black';
  const bg = style === 'black' ? BLACK_BG : dark ? DARK_BG : SITE_BG;
  const ink = dark ? DARK_INK : INK;
  const muted = dark ? DARK_MUTED : MUTED;
  const e = 1 - Math.pow(1 - Math.min(1, Math.max(0, progress)), 3);
  const M = 72;

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = 'top';

  const story = format === 'story';
  const wide = format === 'landscape';
  const safeTop = story ? 250 : M;
  const safeBottom = story ? 310 : M;
  const footerY = H - safeBottom - 34;

  // Optional Prompt-Brush header, then the chart title.
  let titleY = safeTop;
  if (header) {
    ctx.fillStyle = ink;
    ctx.font = `40px ${DELCAN}`;
    ctx.fillText('Prompt-Brush 2.0', M, safeTop);
    const url = 'Prompt-Brush.com';
    ctx.fillText(url, W - M - ctx.measureText(url).width, safeTop);
    titleY = safeTop + (wide ? 88 : 112);
  }
  ctx.fillStyle = ink;
  ctx.font = `48px ${DELCAN}`;
  ctx.fillText(title, M, titleY);

  const regionTop = titleY + (wide ? 80 : 98);
  const regionBottom = footerY - 60;
  const n = items.length;
  const mx = Math.max(...items.map((t) => t.count), 1);
  const shown = (c: number) => String(Math.round(c * e));

  if (kind === 'donut') {
    const sum = items.reduce((s, t) => s + t.count, 0) || 1;
    const DR = wide ? Math.min(230, Math.floor((regionBottom - regionTop) / 2) - 10) : story ? 270 : 230;
    const DRI = Math.round(DR * 0.59);
    const cx = wide ? M + 110 + DR : W / 2;
    const cy = wide ? Math.round((regionTop + regionBottom) / 2) : regionTop + DR;
    // Sweep clockwise to `e` of the full circle.
    let acc = 0;
    items.forEach((t, i) => {
      const startFrac = acc / sum;
      acc += t.count;
      const endFrac = Math.min(acc / sum, e);
      if (endFrac <= startFrac) return;
      const a0 = -Math.PI / 2 + startFrac * Math.PI * 2;
      const a1 = -Math.PI / 2 + endFrac * Math.PI * 2;
      const [tile, r] = TONES[i % TONES.length];
      ctx.beginPath();
      ctx.arc(cx, cy, DR, a0, a1);
      ctx.arc(cx, cy, DRI, a1, a0, true);
      ctx.closePath();
      ctx.fillStyle = dotPattern(ctx, tile, r, ink);
      ctx.fill();
      ctx.strokeStyle = CHART_OUTLINE;
      ctx.lineWidth = 2.5;
      ctx.stroke();
    });
    // Ranked bars in the same tones — beneath the donut, or beside it on a screen.
    const rowH = story ? 74 : 66;
    const barH = story ? 36 : 34;
    const lx = wide ? cx + DR + 120 : M;
    const bx = lx + 330;
    const bw = W - M - 130 - bx;
    const y0 = wide ? Math.round(cy - (n * rowH) / 2 + 8) : cy + DR + (story ? 80 : 60);
    items.forEach((t, i) => {
      const yy = y0 + i * rowH;
      ctx.fillStyle = muted;
      ctx.font = `26px ${FKMONO}`;
      tracked(ctx, t.label.toUpperCase(), lx, yy + 8, 2);
      const [tile, r] = TONES[i % TONES.length];
      halftoneRect(ctx, bx, yy, Math.max(26, Math.round((bw * t.count * e) / mx)), barH, tile, r, ink);
      ctx.fillStyle = ink;
      ctx.font = `${story ? 46 : 44}px ${DELCAN}`;
      ctx.fillText(shown(t.count), bx + bw + 34, yy - 6);
    });
  } else if (kind === 'columns') {
    const slot = (W - 2 * M) / n;
    const colW = Math.min(wide ? 180 : 150, slot - 28);
    const baseY = regionBottom - 46; // baseline; labels sit just below
    const colMaxH = baseY - regionTop - 70; // leave room for the count above
    const [tile, r] = TONES[2];
    items.forEach((t, i) => {
      const h = Math.max(14, Math.round((colMaxH * t.count * e) / mx));
      const center = M + slot * i + slot / 2;
      const x = Math.round(center - colW / 2);
      halftoneRect(ctx, x, baseY - h, colW, h, tile, r, ink);
      const cs = shown(t.count);
      ctx.fillStyle = ink;
      ctx.font = `${story ? 42 : 40}px ${DELCAN}`;
      ctx.fillText(cs, center - ctx.measureText(cs).width / 2, baseY - h - 52);
      ctx.fillStyle = muted;
      ctx.font = `24px ${FKMONO}`;
      tracked(ctx, t.label.toUpperCase(), 0, baseY + 14, 3, center);
    });
  } else {
    // Horizontal ranked bars (e.g. top artists). On a screen the rows share the height.
    const rowH = wide ? Math.min(92, Math.floor((regionBottom - regionTop - 10) / Math.max(1, n))) : story ? 104 : 92;
    const barH = wide ? Math.min(36, Math.round(rowH * 0.42)) : story ? 40 : 36;
    const lblW = 400;
    const bx = M + lblW;
    const bw = W - M - 130 - bx;
    const y0 = regionTop + 10;
    items.forEach((t, i) => {
      const yy = y0 + i * rowH;
      const name = t.label.length > 22 ? t.label.slice(0, 21) + '…' : t.label;
      ctx.fillStyle = muted;
      ctx.font = `26px ${FKMONO}`;
      tracked(ctx, name.toUpperCase(), M, yy + 8, 3);
      const [tile, r] = TONES[i % TONES.length];
      halftoneRect(ctx, bx, yy, Math.max(26, Math.round((bw * t.count * e) / mx)), barH, tile, r, ink);
      ctx.fillStyle = ink;
      ctx.font = `${story ? 46 : 44}px ${DELCAN}`;
      ctx.fillText(shown(t.count), bx + bw + 34, yy - 4);
    });
  }

  ctx.fillStyle = muted;
  ctx.font = `26px ${FKMONO}`;
  tracked(ctx, footnote.toUpperCase(), M, footerY, 3);
}

/** Export one halftone stats chart as a social PNG and trigger the download. */
export async function exportChartImage(
  spec: ChartSpec,
  format: SocialFormat,
  style: Exclude<SocialStyle, 'poster'>,
  opts: ExportOpts = {}
): Promise<void> {
  await ensureFonts();
  const [W, H] = SIZES[format];
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  drawChart(canvas.getContext('2d')!, spec, format, style, opts);
  await download(canvas, `prompt-brush-${spec.slug}-${format}-${style}.png`);
}

/** Load the brand fonts into the document cache — for callers that draw to their own canvas. */
export async function loadShareFonts(): Promise<void> {
  await ensureFonts();
}
